'use client'

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { createSafeJSONStorage } from '@/utils/zustand/safe-local-storage'
import type { IAgentSelectableImageModel } from 'shared'

interface IAgentStoreState {
    variantOverride: string | null
    startingModeOverride: string | null
    /** User's image-model pick for the next created agent. Session-only (not
     *  persisted) so it falls back to the brand's latest DB model on reload. */
    imageModelOverride: IAgentSelectableImageModel | null
    /** User's marketing-channel pick (slug) for the next created agent. Session-only
     *  so it falls back to the brand's latest DB channel on reload. */
    channelOverride: string | null

    setVariantOverride: (variantId: string | null) => void
    setStartingModeOverride: (modeId: string | null) => void
    setImageModelOverride: (imageModel: IAgentSelectableImageModel | null) => void
    setChannelOverride: (channelSlug: string | null) => void
}

/**
 * Zustand store for the superuser-only variant + starting-mode override plus
 * the all-users image-model override. Variant/mode are only consulted when
 * `useIsSuperUser()` returns true — gate in the UI layer before reading.
 */
export const useAgentStore = create<IAgentStoreState>()(
    devtools(
        persist(
            (set) => ({
                variantOverride: null,
                startingModeOverride: null,
                imageModelOverride: null,
                channelOverride: null,
                setVariantOverride: (variantId) => set({ variantOverride: variantId, startingModeOverride: null }),
                setStartingModeOverride: (modeId) => set({ startingModeOverride: modeId }),
                setImageModelOverride: (imageModel) => set({ imageModelOverride: imageModel }),
                setChannelOverride: (channelSlug) => set({ channelOverride: channelSlug }),
            }),
            {
                name: 'agent-settings',
                storage: createSafeJSONStorage(),
                partialize: (state) => ({
                    variantOverride: state.variantOverride,
                    startingModeOverride: state.startingModeOverride,
                }),
            }
        ),
        { name: 'agent-store' }
    )
)
